import React, { useState, useEffect, useCallback } from 'react';
import { RefreshCw, Search, Trophy, ChevronDown, ChevronUp } from 'lucide-react';
import api from '../utils/api';

export default function ReferralsScreen() {
  const [tab, setTab] = useState('leaderboard'); // leaderboard | all | earnings
  const [leaderboard, setLeaderboard] = useState([]);
  const [referrals, setReferrals] = useState([]);
  const [earnings, setEarnings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [expandedRef, setExpandedRef] = useState(null);

  const loadData = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      if (tab === 'leaderboard') {
        const data = await api.getLeaderboard();
        setLeaderboard(data.leaderboard || data.data || data || []);
      } else if (tab === 'all') {
        const data = await api.getReferrals(200);
        setReferrals(data.referrals || data.data || data || []);
      } else if (tab === 'earnings') {
        const data = await api.getReferralEarnings({ limit: 200 });
        setEarnings(data.earnings || data.data || data || []);
      }
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [tab]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  const formatNumber = (num) => {
    if (num === null || num === undefined) return '—';
    return Math.round(Number(num)).toLocaleString('ru-RU');
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '—';
    try {
      return new Date(dateStr).toLocaleString('ru-RU', {
        day: '2-digit', month: '2-digit', year: '2-digit', hour: '2-digit', minute: '2-digit',
      });
    } catch { return dateStr; }
  };

  const medalColors = ['text-yellow-400', 'text-gray-300', 'text-amber-600'];

  return (
    <div>
      <div className="flex items-center justify-between mb-6">
        <h2 className="text-xl font-bold text-white">Рефералы</h2>
        <button
          onClick={loadData}
          disabled={loading}
          className="p-2 bg-gray-800 rounded-lg border border-gray-700 text-gray-400 hover:text-white disabled:opacity-50"
        >
          <RefreshCw size={16} className={loading ? 'animate-spin' : ''} />
        </button>
      </div>

      {/* Tabs */}
      <div className="flex gap-2 mb-4">
        {[
          { id: 'leaderboard', label: 'Лидерборд' },
          { id: 'all', label: 'Все связи' },
          { id: 'earnings', label: 'Начисления' },
        ].map(t => (
          <button
            key={t.id}
            onClick={() => setTab(t.id)}
            className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
              tab === t.id
                ? 'bg-purple-600 text-white'
                : 'bg-gray-800 text-gray-400 hover:text-white border border-gray-700'
            }`}
          >
            {t.label}
          </button>
        ))}
      </div>

      {/* Search */}
      <div className="relative mb-4">
        <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-500" />
        <input
          type="text"
          placeholder="Поиск..."
          value={searchQuery}
          onChange={e => setSearchQuery(e.target.value)}
          className="w-full bg-gray-800 border border-gray-700 rounded-lg pl-9 pr-4 py-2 text-sm text-white placeholder-gray-500 focus:outline-none focus:border-purple-500"
        />
      </div>

      {error && (
        <div className="bg-red-500/10 border border-red-500/30 rounded-xl p-4 mb-4 text-red-400 text-sm">
          {error}
        </div>
      )}

      {/* Leaderboard */}
      {tab === 'leaderboard' && (
        <div className="space-y-2">
          {loading ? (
            Array.from({ length: 5 }).map((_, i) => (
              <div key={i} className="bg-gray-800 rounded-xl border border-gray-700/50 p-4">
                <div className="h-5 bg-gray-700 rounded animate-pulse" />
              </div>
            ))
          ) : leaderboard.length === 0 ? (
            <div className="bg-gray-800 rounded-xl border border-gray-700/50 p-8 text-center text-gray-500">
              Нет данных
            </div>
          ) : (
            leaderboard
              .filter(item => {
                if (!searchQuery) return true;
                const q = searchQuery.toLowerCase();
                return String(item.userId || item.user_id || '').includes(q) ||
                  String(item.username || '').toLowerCase().includes(q);
              })
              .map((item, i) => {
                const uid = item.userId || item.user_id;
                const isExpanded = expandedRef === uid;
                const refList = item.referrals || [];
                return (
                  <div key={i} className="bg-gray-800 rounded-xl border border-gray-700/50 overflow-hidden">
                    <button
                      onClick={() => setExpandedRef(isExpanded ? null : uid)}
                      className="w-full p-4 flex items-center gap-4 hover:bg-gray-700/30 transition-colors text-left"
                    >
                      <div className="w-8 text-center shrink-0">
                        {i < 3 ? (
                          <Trophy size={20} className={medalColors[i]} />
                        ) : (
                          <span className="text-gray-500 text-sm font-bold">{i + 1}</span>
                        )}
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="text-sm text-white font-medium truncate">
                          {item.username ? `@${item.username}` : `ID: ${uid}`}
                        </p>
                        <p className="text-xs text-gray-500">
                          {item.firstName || item.first_name || ''}
                        </p>
                      </div>
                      <div className="text-right shrink-0 flex items-center gap-3">
                        <div>
                          <p className="text-sm font-bold text-white">{formatNumber(item.referralsCount ?? item.count)} реф.</p>
                          {(() => {
                            const earned = typeof item.earnings === 'object' && item.earnings !== null
                              ? item.earnings.totalEarned
                              : (item.totalEarnings ?? item.earnings);
                            const count = typeof item.earnings === 'object' && item.earnings !== null
                              ? item.earnings.earningsCount
                              : null;
                            if (earned === undefined || earned === null) return null;
                            return (
                              <p className="text-xs text-green-400">
                                {Number(earned).toFixed(2)} USDT
                                {count ? ` · ${count}` : ''}
                              </p>
                            );
                          })()}
                        </div>
                        {refList.length > 0 && (
                          isExpanded
                            ? <ChevronUp size={14} className="text-gray-400" />
                            : <ChevronDown size={14} className="text-gray-400" />
                        )}
                      </div>
                    </button>

                    {isExpanded && refList.length > 0 && (
                      <div className="border-t border-gray-700 bg-gray-800/50 p-3">
                        <p className="text-xs text-gray-400 font-medium mb-2">
                          Приглашённые ({refList.length})
                        </p>
                        <div className="space-y-1.5 max-h-64 overflow-y-auto">
                          {refList.map((ref, j) => (
                            <div
                              key={ref.userId || j}
                              className="flex items-center justify-between bg-gray-700/30 rounded-lg px-3 py-2"
                            >
                              <div className="flex items-center gap-2 min-w-0">
                                <div className="w-6 h-6 rounded-full bg-gray-600 flex items-center justify-center text-white text-xs font-bold shrink-0">
                                  {(ref.username || ref.userId || '?')[0]?.toUpperCase()}
                                </div>
                                <span className="text-xs text-white truncate">
                                  {ref.username ? `@${ref.username}` : `ID: ${ref.userId}`}
                                </span>
                              </div>
                              <span className="text-xs text-gray-500 shrink-0 ml-2">
                                {formatDate(ref.date || ref.createdAt)}
                              </span>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                );
              })
          )}
        </div>
      )}

      {/* All referrals */}
      {tab === 'all' && (
        <div className="bg-gray-800 rounded-xl border border-gray-700/50 overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="text-gray-400 text-xs border-b border-gray-700">
                  <th className="text-left p-3">Реферер</th>
                  <th className="text-left p-3">Реферал</th>
                  <th className="text-right p-3">Дата</th>
                </tr>
              </thead>
              <tbody>
                {loading ? (
                  <tr><td colSpan={3} className="p-8 text-center text-gray-500">Загрузка...</td></tr>
                ) : referrals.length === 0 ? (
                  <tr><td colSpan={3} className="p-8 text-center text-gray-500">Нет данных</td></tr>
                ) : (
                  referrals
                    .filter(r => {
                      if (!searchQuery) return true;
                      const q = searchQuery.toLowerCase();
                      return String(r.referrerId || '').toLowerCase().includes(q) ||
                        String(r.referredId || '').includes(q) ||
                        String(r.referredUsername || '').toLowerCase().includes(q);
                    })
                    .map((ref, i) => (
                      <tr key={i} className="border-t border-gray-700/50 hover:bg-gray-700/30">
                        <td className="p-3 text-white text-xs">{ref.referrerId}{ref.referrerUsername ? ` (@${ref.referrerUsername})` : ''}</td>
                        <td className="p-3 text-gray-300 text-xs">{ref.referredUsername ? `@${ref.referredUsername}` : ref.referredId}</td>
                        <td className="p-3 text-right text-gray-400 text-xs">{ref.createdAt || '—'}</td>
                      </tr>
                    ))
                )}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Earnings */}
      {tab === 'earnings' && (
        <div className="bg-gray-800 rounded-xl border border-gray-700/50 overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="text-gray-400 text-xs border-b border-gray-700">
                  <th className="text-left p-3">Реферер</th>
                  <th className="text-right p-3">Сумма</th>
                  <th className="text-center p-3">Статус</th>
                  <th className="text-right p-3">Дата</th>
                </tr>
              </thead>
              <tbody>
                {loading ? (
                  <tr><td colSpan={4} className="p-8 text-center text-gray-500">Загрузка...</td></tr>
                ) : earnings.length === 0 ? (
                  <tr><td colSpan={4} className="p-8 text-center text-gray-500">Нет данных</td></tr>
                ) : (
                  earnings
                    .filter(e => {
                      if (!searchQuery) return true;
                      return String(e.referrerId || e.referrer_id || '').includes(searchQuery);
                    })
                    .map((earning, i) => {
                      const usdt = earning.earningAmountUsdt;
                      const cny = earning.earningAmountCny ?? earning.amount;
                      const hasUsdt = usdt !== null && usdt !== undefined;
                      return (
                      <tr key={i} className="border-t border-gray-700/50 hover:bg-gray-700/30">
                        <td className="p-3 text-white text-xs">{earning.referrerId}</td>
                        <td className="p-3 text-right text-green-400">
                          {hasUsdt
                            ? `${Number(usdt).toFixed(4)} USDT`
                            : `${formatNumber(cny)} ¥`}
                        </td>
                        <td className="p-3 text-center">
                          <span className={`px-2 py-0.5 rounded-full text-xs ${
                            earning.status === 'paid'
                              ? 'bg-green-500/20 text-green-400'
                              : 'bg-yellow-500/20 text-yellow-400'
                          }`}>
                            {earning.status === 'paid' ? 'Выплачено' : 'Ожидает'}
                          </span>
                        </td>
                        <td className="p-3 text-right text-gray-400 text-xs">{earning.createdAt || earning.created_at || '—'}</td>
                      </tr>
                      );
                    })
                )}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
}
